import React, { useEffect, useMemo, useState } from 'react';

import DoneScreen from './DoneScreen';
import FormScreen from './FormScreen';
import ReviewScreen from './ReviewScreen';
import WelcomeScreen from './WelcomeScreen';
import useFormDraft from './useFormDraft';
import { fetchScopeCatalog, fetchScopeForm, normalizeCatalog, submitScopeForm } from './api';
import InvalidTokenScreen from './InvalidTokenScreen';
import { selectedModifierIdsFromDraft, selectedScopeIdsFromDraft } from './scopeSelection';
import { formatLocaleDate } from '../../Utils/HelperFunctions';
import { RootStyles as S } from './styles';

const tokenFromPath = () => {
  const parts = window.location.pathname.split('/').filter(Boolean);
  const idx = parts.indexOf('scope-form');
  return idx >= 0 ? parts[idx + 1] || '' : '';
};

const buildSubmitBody = ({ draft, email, trades }) => {
  const selected_scope_ids = selectedScopeIdsFromDraft(trades, draft);
  const selected_element_ids = Object.keys(draft.elements);
  const allowedModifiers = selectedModifierIdsFromDraft(trades, draft);
  const modifier_answers = Object.entries(draft.modifiers)
    .filter(([modifier_id, v]) => {
      if (!allowedModifiers.has(modifier_id)) return false;
      if (!v) return false;
      const opts = v.selected_options || [];
      const text = v.text_value || '';
      return opts.length > 0 || text.length > 0;
    })
    .map(([modifier_id, v]) => ({
      modifier_id,
      selected_options: v.selected_options || [],
      text_value: v.text_value || ''
    }));
  const selectedScopeSet = new Set(selected_scope_ids);
  const notesEntries = (trades || [])
    .filter(trade => (trade.scopes || []).some(sc => selectedScopeSet.has(sc.scope_id)))
    .map(trade => [trade.trade_id, (draft.notes[trade.trade_id] || '').trim()])
    .filter(([, note]) => note.length > 0);
  const notes_data = notesEntries.length ? Object.fromEntries(notesEntries) : undefined;
  return {
    submitted_by_email: (email || '').trim(),
    selected_scope_ids,
    selected_element_ids,
    modifier_answers,
    ...(notes_data ? { notes_data } : {})
  };
};

const CustomerScopeForm = () => {
  const [token] = useState(tokenFromPath);
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState(null);
  const [invalid, setInvalid] = useState(null);
  const [screen, setScreen] = useState('welcome');
  const [email, setEmail] = useState('');
  const [activeTradeIdx, setActiveTradeIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submissionResult, setSubmissionResult] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState('');

  useEffect(() => {
    if (!token) {
      setInvalid('No token in the URL.');
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    fetchScopeForm(token, { signal: controller.signal }).then(({ data, error }) => {
      if (controller.signal.aborted) return;
      setLoading(false);
      if (error) {
        const detail = typeof error === 'string' ? error : error.detail;
        setInvalid(detail || null);
        return;
      }
      setPayload(data);
      if (data?.customer_email) setEmail(data.customer_email);
    });
    return () => controller.abort();
  }, [token]);

  const trades = useMemo(() => {
    if (catalog?.trades) return catalog.trades;
    if (payload?.trades) return normalizeCatalog(payload).trades || [];
    return [];
  }, [catalog, payload]);

  useEffect(() => {
    if (!trades.length) return;
    if (activeTradeIdx >= trades.length) setActiveTradeIdx(0);
  }, [trades, activeTradeIdx]);

  const tradesInScope = payload?.trades_in_scope;
  const currentSelections = payload?.current_selections;
  const rawStatus = String(payload?.scope_form_status || payload?.state || '').toUpperCase();
  const state = rawStatus === 'RESUBMITTED' ? 'SUBMITTED' : rawStatus;
  const readOnly = state === 'SUBMITTED';
  const submittedAt =
    payload?.submitted_at || currentSelections?.submitted_at || payload?.updated_at || null;
  const aeName = payload?.account_executive?.name || payload?.ae_name;
  const prospectCompany = payload?.prospect_company_name || payload?.company_name;
  const reopenReason = payload?.reopen_reason || currentSelections?.reopen_reason || '';
  const serverRevision =
    payload?.current_version?.version_id ??
    payload?.current_version?.version ??
    submittedAt ??
    payload?.updated_at ??
    null;

  const { draft, savedAt, toggleScope, toggleElement, setModifierAnswer, setNote, clearTrade, purge } = useFormDraft({
    token,
    tradesInScope,
    serverRevision,
    preferServer: readOnly
  });

  const hasUnsavedContent =
    !readOnly &&
    !submissionResult &&
    (Object.keys(draft.scopes).length > 0 ||
      Object.keys(draft.elements).length > 0 ||
      Object.keys(draft.modifiers).length > 0 ||
      Object.values(draft.notes).some(v => (v || '').trim()));

  useEffect(() => {
    if (!hasUnsavedContent) return undefined;
    const handler = e => {
      // Chrome, Firefox and modern browsers ignore the message and show their default —
      // returnValue must still be set for the prompt to appear.
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedContent]);

  const handleStart = async submittedEmail => {
    setEmail(submittedEmail);
    if (catalog) {
      setScreen('form');
      window.scrollTo({ top: 0 });
      return;
    }
    // Prefer trades already on the public form GET payload when present.
    const fromPayload = payload?.trades?.length ? normalizeCatalog(payload) : null;
    if (fromPayload?.trades?.length) {
      setCatalog(fromPayload);
      setScreen('form');
      window.scrollTo({ top: 0 });
      return;
    }
    setStarting(true);
    setStartError('');
    const { data, error } = await fetchScopeCatalog(token);
    setStarting(false);
    if (error) {
      const detail = typeof error === 'string' ? error : error.detail || 'Failed to load scope form.';
      setStartError(detail);
      return;
    }
    setCatalog(normalizeCatalog(data));
    setScreen('form');
    window.scrollTo({ top: 0 });
  };

  const handleEditTrade = tradeId => {
    const idx = trades.findIndex(t => t.trade_id === tradeId);
    if (idx >= 0) setActiveTradeIdx(idx);
    setScreen('form');
    window.scrollTo({ top: 0 });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError('');
    const body = buildSubmitBody({ draft, email, trades });
    const { data, error } = await submitScopeForm(token, body);
    setSubmitting(false);
    if (error) {
      const detail = typeof error === 'string' ? error : error.detail || 'Submission failed — please try again.';
      setSubmitError(detail);
      return;
    }
    setSubmissionResult(data || {});
    purge();
    setScreen('done');
    window.scrollTo({ top: 0 });
  };

  if (loading) {
    return <S.LoadingScreen>Loading your scope form…</S.LoadingScreen>;
  }

  if (invalid !== null || !payload) {
    return <InvalidTokenScreen reason={invalid} />;
  }

  if (screen === 'welcome') {
    const submittedOn = submittedAt ? formatLocaleDate(submittedAt) : '';
    const isReopened = state === 'REOPENED';
    return (
      <WelcomeScreen
        aeName={aeName}
        prospectCompany={prospectCompany}
        tradeCount={trades.length || null}
        timeEstimate={payload.time_estimate}
        showWelcomeBack={!!currentSelections || readOnly}
        welcomeBackTitle={
          readOnly
            ? submittedOn
              ? `Submitted on ${submittedOn}`
              : 'Already submitted'
            : isReopened
              ? 'Your scope form was reopened for edits'
              : 'Picking up where you left off'
        }
        welcomeBackBody={
          readOnly
            ? 'This is the scope you sent us. Open it to review — no changes needed unless your account team reopens the form.'
            : isReopened
              ? reopenReason ||
                'Your account team unlocked it so you can revise and resubmit. Your previous answers are all still here.'
              : 'Your prior answers are already on this form — review or change them, then re-submit.'
        }
        initialEmail={email}
        starting={starting}
        startError={startError}
        onStart={handleStart}
        readOnly={readOnly}
        isReopened={isReopened}
        submittedOn={submittedOn}
      />
    );
  }

  if (screen === 'form') {
    return (
      <FormScreen
        trades={trades}
        activeTradeIdx={activeTradeIdx}
        onSelectTrade={setActiveTradeIdx}
        draft={draft}
        savedAt={savedAt}
        onToggleScope={toggleScope}
        onToggleElement={toggleElement}
        onModifierChange={setModifierAnswer}
        onNote={setNote}
        onClearTrade={clearTrade}
        onReview={() => {
          setScreen('review');
          window.scrollTo({ top: 0 });
        }}
        readOnly={readOnly}
        state={state}
        reopenReason={reopenReason}
        headerTitle={prospectCompany || 'Scope form'}
      />
    );
  }

  if (screen === 'review') {
    return (
      <ReviewScreen
        trades={trades}
        draft={draft}
        email={email}
        onEmail={setEmail}
        onEditTrade={handleEditTrade}
        onBackToForm={() => {
          setScreen('form');
          window.scrollTo({ top: 0 });
        }}
        onSubmit={handleSubmit}
        submitting={submitting}
        submitError={submitError}
        readOnly={readOnly}
        isReopened={state === 'REOPENED'}
        aeName={aeName}
      />
    );
  }

  if (screen === 'done') {
    return (
      <DoneScreen
        email={email}
        aeName={aeName}
        submittedAt={submissionResult?.submitted_at || currentSelections?.submitted_at}
        version={submissionResult?.version}
      />
    );
  }

  return null;
};

export default CustomerScopeForm;
